import { Config } from "services/configuration/eatSmartConfig";
import type { Maybe } from "types";
import { validate as uuidValidate } from "uuid";

/**
 * Sets new fridgeId in localStorage, updates URL search parameter, and reloads the page
 * @param currentFridgeId Optional current fridgeId to show in prompt
 * @param errorMessage Optional error message to show in prompt
 */
export const setupNewFridgeId = (currentFridgeId?: string, errorMessage?: string) => {
	const message = errorMessage
		? errorMessage
		: currentFridgeId
			? `Current fridgeId "${currentFridgeId}" is invalid. Please provide a valid UUID`
			: "Please provide a valid fridgeId UUID";

	// biome-ignore lint/suspicious/noAlert: intentional use of prompt for fridge ID setup
	const newFridgeId = prompt(message);

	if (newFridgeId === null) {
		// If user cancels the prompt, remove the fridgeId from localStorage
		localStorage.removeItem("fridgeId");
	} else if (validateFridgeId(newFridgeId)) {
		localStorage.setItem("fridgeId", newFridgeId);

		// Update URL search parameter
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.set("fridgeId", newFridgeId);

		// Update the URL and reload the page
		const newUrl = new URL(window.location.href);
		newUrl.search = searchParams.toString();
		window.location.href = newUrl.toString();
	} else {
		// Recursively ask for valid UUID if input is invalid
		setupNewFridgeId(newFridgeId);
	}
};

/**
 * Gets query parameter from URL or localStorage.
 * @param paramName Query parameter name.
 * @param paramDefaultValue Parameter default value.
 * @returns Current parameter value.
 */
const getQueryParamByName = (paramName: string, paramDefaultValue: string) => {
	const paramFromUrl = new URLSearchParams(window.location.search).get(paramName);
	const paraFromLocalStorage = localStorage.getItem(paramName);

	// Return the param from local storage if available
	if (paraFromLocalStorage !== null && paramFromUrl === null) {
		return paraFromLocalStorage;
	}

	// Update local storage with the query param and return it
	if (paramFromUrl !== null) {
		localStorage.setItem(paramName, paramFromUrl);
		return paramFromUrl;
	}

	localStorage.setItem(paramName, paramDefaultValue);
	return paramDefaultValue;
};

/**
 * Returns fridgeId from query params or localStorage
 */
export const getFridgeId = () => {
	const fridgeId = getQueryParamByName("fridgeId", "");
	const isUuidValid = validateFridgeId(fridgeId);

	if (isUuidValid) {
		return fridgeId;
	}

	// Set up a new fridge ID if none is found or if it's invalid
	setupNewFridgeId(fridgeId);
	return undefined;
};

export enum TabletTypeEnum {
	Master = "master",
	Standard = "standard",
}

export const getTabletTypeFromUrl = () => {
	const tabletTypeFromQueryParam = new URLSearchParams(window.location.search).get("tabletType");

	if (tabletTypeFromQueryParam !== null && tabletTypeFromQueryParam === TabletTypeEnum.Master) {
		localStorage.setItem("tabletType", TabletTypeEnum.Master);
	} else {
		localStorage.setItem("tabletType", TabletTypeEnum.Standard);
	}
};

export const getTabletType = () => {
	const tabletTypeFromLocalStorage = localStorage.getItem("tabletType");

	if (tabletTypeFromLocalStorage !== null) {
		return tabletTypeFromLocalStorage;
	}

	getTabletTypeFromUrl();
};

/**
 * Gets current instance locale. Primary source is URL.
 * @returns Current instance default locale.
 */
export const getLocale = () => getQueryParamByName(Config.common.localeLocalStorageKey, Config.common.defaultLocale);
export const getIsMasterTablet = () => {
	const tabletType = getTabletType();
	return tabletType === TabletTypeEnum.Master;
};

export const setFridgeNameToLocalStorage = (fridgeName: Maybe<string>) => {
	const currentFridgeName = localStorage.getItem("fridgeName");
	if (fridgeName && currentFridgeName !== fridgeName) {
		localStorage.setItem("fridgeName", fridgeName);
	}
};

export const getFridgeName = () => localStorage.getItem("fridgeName");

/**
 * Validates fridgeId and provides appropriate feedback
 * @param fridgeId The fridgeId to validate
 * @returns True if valid UUID, false otherwise
 */
export const validateFridgeId = (fridgeId: string): boolean => {
	if (!fridgeId || fridgeId.length === 0) {
		return false;
	}
	return uuidValidate(fridgeId);
};
